import { useEffect, useId, useRef, useState } from "react";
import type { CountryConfig } from "@orvis/core";
import { adsReady, inlineBannersEnabled } from "../ads.js";
import { trackEvent } from "../analytics.js";
import { InlineBanner, type InlineBannerGeometry } from "../inlineBanner.js";
import { useTvMode } from "../hooks/useTvMode.js";

/**
 * In-feed ad slot — drop it into a list like any other row (ported from
 * Itinero).
 *
 * On Android phones the ad is a real AdMob inline adaptive banner (native
 * view) placed over this element by the app-local InlineBanner plugin. The
 * plugin moves it with the WebView's scroll every frame, so it scrolls with
 * the list like a row, and clips it under the sticky header / bottom tab bar
 * (`data-ad-clip="top" | "bottom"` elements).
 *
 * This element only reserves the space: a skeleton while the ad loads, then
 * the banner's exact height. On no-fill the slot collapses entirely. The
 * request is only made once the slot is about to scroll on screen and nothing
 * covers it, so we never load (and count) an ad nobody can see
 * (docs/ads-policy.md).
 *
 * The banner is a native view, so it paints ABOVE every DOM element. It steps
 * aside (hidden, not reloaded) while:
 *   • a video is fullscreen,
 *   • a dialog is open, or a floating layer marked `data-ad-avoid` overlaps it,
 *   • the keyboard is up (an editable element has focus).
 *
 * Renders nothing on web, iOS and Android TV — those keep the bottom banner
 * (see ads.ts).
 */

const AVOID_SELECTOR = '[data-ad-avoid], [role="dialog"], [role="menu"], [role="listbox"]';

/** Height reserved while the ad request is in flight. */
const LOADING_HEIGHT_PX = 60;

/** Start the request once the slot is this many viewports away or closer. */
const PRELOAD_VIEWPORTS = 0.5;

type Status = "idle" | "loading" | "loaded" | "failed";

export function InlineAd({
  country,
  slot,
  maxHeight = 100,
}: {
  country: CountryConfig;
  /** Analytics label for where the slot lives (e.g. "tv", "radio"). */
  slot: string;
  /** Tallest ad (dp) AdMob may return for this slot. */
  maxHeight?: number;
}) {
  const tv = useTvMode();
  const adId = country.ads.bannerUnitId;
  const enabled = !!adId && inlineBannersEnabled(tv);
  const ref = useRef<HTMLDivElement>(null);
  const bannerId = `${slot}-${useId()}`;
  const [status, setStatus] = useState<Status>("idle");
  const [height, setHeight] = useState(LOADING_HEIGHT_PX);

  useEffect(() => {
    if (!enabled || !adId) return;
    const el = ref.current;
    if (!el) return;

    let disposed = false;
    let requested = false;
    let created = false;
    let raf = 0;
    let last: InlineBannerGeometry | null = null;
    let shown = true;
    const handles: Array<Promise<{ remove: () => Promise<void> }>> = [];

    const measure = (): InlineBannerGeometry & { width: number } => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      let clipTop = 0;
      let clipBottom = 0;
      document.querySelectorAll<HTMLElement>("[data-ad-clip]").forEach((bar) => {
        const r = bar.getBoundingClientRect();
        if (r.height === 0) return;
        if (bar.dataset.adClip === "top") clipTop = Math.max(clipTop, r.bottom);
        else clipBottom = Math.max(clipBottom, vh - r.top);
      });
      return { x: rect.left, y: rect.top + window.scrollY, width: rect.width, clipTop, clipBottom };
    };

    const request = async () => {
      requested = true;
      setStatus("loading");
      if (!(await adsReady()) || disposed) {
        if (!disposed) setStatus("failed");
        return;
      }
      const g = measure();
      last = g;
      try {
        await InlineBanner.create({ id: bannerId, adId, maxHeight, ...g });
        created = true;
        if (disposed) {
          void InlineBanner.remove({ id: bannerId });
          return;
        }
        if (shouldYield(el, measure())) {
          shown = false;
          void InlineBanner.setVisible({ id: bannerId, visible: false });
        }
      } catch (err) {
        // e.g. an older native build without the InlineBanner plugin.
        trackEvent("ad_request_failed", {
          slot,
          code: -1,
          message: String(err instanceof Error ? err.message : err).slice(0, 100),
        });
        setStatus("failed");
      }
    };

    // Layout changes (the TV player opening, images loading) move the
    // placeholder without a scroll. Check once per frame and only cross the
    // bridge when something actually moved — plain scrolling leaves every
    // value unchanged, native handles that.
    const tick = () => {
      if (disposed) return;
      raf = requestAnimationFrame(tick);
      if (!requested) {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (
          r.top < vh * (1 + PRELOAD_VIEWPORTS) &&
          r.bottom > -vh * PRELOAD_VIEWPORTS &&
          !shouldYield(el, measure())
        ) {
          void request();
        }
        return;
      }
      if (!created) return;
      const g = measure();
      const show = !shouldYield(el, g);
      if (show !== shown) {
        shown = show;
        void InlineBanner.setVisible({ id: bannerId, visible: show });
      }
      if (
        last &&
        Math.abs(g.x - last.x) < 0.5 &&
        Math.abs(g.y - last.y) < 0.5 &&
        Math.abs(g.clipTop - last.clipTop) < 0.5 &&
        Math.abs(g.clipBottom - last.clipBottom) < 0.5
      ) {
        return;
      }
      last = g;
      void InlineBanner.update({ id: bannerId, ...g });
    };

    handles.push(
      InlineBanner.addListener("bannerLoaded", (e) => {
        if (e.id !== bannerId) return;
        if (e.height > 0) setHeight(e.height);
        setStatus("loaded");
      }),
      InlineBanner.addListener("bannerFailed", (e) => {
        if (e.id !== bannerId) return;
        trackEvent("ad_request_failed", {
          slot,
          code: e.code,
          message: String(e.message).slice(0, 100),
        });
        setStatus("failed");
      }),
    );
    raf = requestAnimationFrame(tick);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      handles.forEach((h) => void h.then((x) => x.remove()));
      if (created) void InlineBanner.remove({ id: bannerId });
    };
  }, [enabled, adId, slot, bannerId, maxHeight]);

  if (!enabled || status === "failed") return null;

  return (
    <div
      ref={ref}
      aria-hidden
      data-ad-slot={slot}
      className={
        "w-full overflow-hidden rounded-lg transition-[height] duration-200 " +
        (status === "loaded" ? "" : "animate-pulse bg-card")
      }
      style={{ height }}
    />
  );
}

/**
 * True when the banner must step aside so it never covers the UI (see the
 * component doc). Runs every frame — keep it cheap.
 */
function shouldYield(el: HTMLElement, g: InlineBannerGeometry): boolean {
  if (document.fullscreenElement) return true;
  if (isEditing()) return true;

  // The part of the banner actually on screen (it's clipped to the band
  // between the sticky header and the bottom bar).
  const r = el.getBoundingClientRect();
  const top = Math.max(r.top, g.clipTop);
  const bottom = Math.min(r.bottom, window.innerHeight - g.clipBottom);
  if (bottom <= top || r.width === 0) return false;

  for (const layer of document.querySelectorAll<HTMLElement>(AVOID_SELECTOR)) {
    if (layer === el || el.contains(layer)) continue;
    const l = layer.getBoundingClientRect();
    if (l.width === 0 || l.height === 0) continue;
    if (l.left < r.right && l.right > r.left && l.top < bottom && l.bottom > top) return true;
  }
  return false;
}

/** Keyboard is (or is about to be) up: something editable has focus. */
function isEditing(): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (!a) return false;
  if (a.isContentEditable || a.tagName === "TEXTAREA" || a.tagName === "SELECT") return true;
  if (a.tagName !== "INPUT") return false;
  const type = (a as HTMLInputElement).type;
  return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file"].includes(type);
}
